import { Injectable, inject } from '@angular/core';
import { Document, SignedURLDownloadResponse, Upload } from '@ceres/domain';
import { HttpParams } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { map, mergeMap, toArray, switchMap } from 'rxjs/operators';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { ImpersonatedHttpClient, DocumentUploadService } from '@ceres/shared/services';
import { environment } from '../../../environments/environment';
@Injectable({
    providedIn: 'root'
})
export class ContractDocumentService {
    private httpClient = inject(ImpersonatedHttpClient);
    private documentUploadService = inject(DocumentUploadService);

    public readonly fileUpload$: Observable<Map<string, Upload>> = this.documentUploadService.fileUpload$;

    private documentUrl = `${environment.edgeService}/documents`;

    // USED IN CONTRACT COMPONENTS
    public getDocuments(contractId: string) {
        return this.httpClient
            .get<Document[]>(`${environment.edgeService}/documents/contracts/${contractId}`)
            .toPromise();
    }

    // USED IN CONTRACT COMPONENTS AND PROJECT DETAILS
    public getOnlyActiveDocuments(contractId: string): Promise<Document[]> {
        return this.httpClient
            .get<Document[]>(`${environment.edgeService}/documents/contracts/${contractId}`, {
                params: { active: 'true' }
            })
            .toPromise();
    }

    // USED IN CONTRACT LIST COMPONENT
    public downloadFile(id: number, name: string) {
        this.httpClient
            .get<SignedURLDownloadResponse>(`${environment.edgeService}/documents/document/${id}`)
            .pipe(switchMap((res) => this.httpClient.get(res.url, { responseType: 'blob' as const })))
            .subscribe(
                (res) => {
                    saveAs(res, name);
                },
                (err) => console.error('An Error occured:', err)
            );
    }

    public downloadFolder(folderId: number, contractName: string) {
        this.httpClient
            .get<Document[]>(`${environment.edgeService}/documents/folder/${folderId}`)
            .pipe(
                map((documents) => (documents.length > 0 ? documents : throwError('No files found'))),
                mergeMap((documents) => from(documents)),
                switchMap((document) =>
                    this.httpClient.get<SignedURLDownloadResponse>(
                        `${environment.edgeService}/documents/document/${document.id}`
                    )
                ),
                switchMap((res) =>
                    this.httpClient.get(res.url, { responseType: 'blob' as const }).pipe(
                        map((data) => {
                            return { data, name: res.name };
                        })
                    )
                ),

                toArray(),
                map((res) => {
                    const zip: JSZip = new JSZip();
                    for (const document of res) {
                        zip.file(document.name, document.data);
                        return zip;
                    }
                }),
                switchMap((zip) => from(zip.generateAsync({ type: 'blob' })))
            )
            .subscribe(
                (file) => {
                    const zipFilename = 'GMS Offer_' + contractName + '.zip';
                    saveAs(file, zipFilename);
                },
                (err) => console.error('An Error occured:', err)
            );
    }

    // USED IN CONTRACT EDIT COMPONENT
    public delete(ids: number[]) {
        let params = new HttpParams();
        ids.forEach((id) => (params = params.append('id', `${id}`)));
        return this.httpClient.delete(`${environment.edgeService}/documents/document`, {
            params
        });
    }

    // USED IN CONTRACT COMPONENTS
    public async uploadMultiple(files: File[], folderId: number) {
        return this.documentUploadService
            .uploadMultiple(this.httpClient, this.documentUrl, files, folderId)
            .toPromise();
    }

    // USED IN CONTRACT EDIT COMPONENT
    public setInactive(id: number) {
        return this.httpClient.patch<Document>(`${environment.edgeService}/documents/document/${id}`, {
            archived: true
        });
    }

    public getDocumentsByProjectNumber(projectNumber: string) {
        return this.httpClient
            .get<any>(`${environment.edgeService}/documents/contracts/projects/${projectNumber}`, {})
            .toPromise();
    }

    public async convertDocumentToPdf(documentId: number, archived?: boolean) {
        return this.httpClient
            .post<Document>(`${environment.edgeService}/documents/document/${documentId}/convertToPdf`, { archived })
            .toPromise();
    }
}
